import * as React from 'react';

import {
  proficientDiagnostic,
  formatDateToMMDDYYYY,
  diagnosticLevelToActivityId,
  diagnosticLevelToUnitTemplateId,
  STARTER,
  INTERMEDIATE,
  ADVANCED,
  TROUBLE_PROCESSING_MESSAGE,
  DemonstratedProficiencyFlag,
  RecommendedFlag,
} from '../shared'
import { StudentHistoryInterface, LeveledAssignmentsInterface, DiagnosticsAssignedInterface, } from '../interfaces'
import { LightButtonLoadingSpinner, Tooltip, titleCase, } from '../../../../../Shared/index'
import { requestPost, } from '../../../../../../modules/request'

interface IndividualStudentRecommendationCardProps {
  classroomId: number;
  diagnosticLevel: string;
  previouslyAssignedDiagnosticsByLevel: DiagnosticsAssignedInterface;
  recommendations: LeveledAssignmentsInterface;
  studentHistory: StudentHistoryInterface;
  getPreviouslyAssignedDiagnosticDataForClassroom: () => void;
  useELLDiagnostics: boolean;
  firstName: string;
}

const IndividualStudentRecommendationCard = ({
  classroomId,
  diagnosticLevel,
  previouslyAssignedDiagnosticsByLevel,
  recommendations,
  studentHistory,
  getPreviouslyAssignedDiagnosticDataForClassroom,
  useELLDiagnostics,
  firstName,
}: IndividualStudentRecommendationCardProps) => {
  const [assigning, setAssigning] = React.useState<Boolean>(false)

  const previewLink = (diagnosticLevel) => {
    const activityId = diagnosticLevelToActivityId(useELLDiagnostics)[diagnosticLevel]
    return `/activity_sessions/anonymous?activity_id=${activityId}`
  }

  const recommendation = recommendations[diagnosticLevel]
  const studentLearningSequences = studentHistory.student_learning_sequences[diagnosticLevel]

  const diagnosticLevelForPreviousAssignments = useELLDiagnostics ? `ell_${diagnosticLevel}` : diagnosticLevel

  const previousAssignmentsForLevel = previouslyAssignedDiagnosticsByLevel[diagnosticLevelForPreviousAssignments]
  const previousAssignmentRecord = previousAssignmentsForLevel?.find(record => record.user_id === studentHistory.user_id)

  const studentIsRecommendedThisDiagnostic = recommendation.student_ids.includes(studentHistory.user_id)

  const proficientDiagnosticRecord = proficientDiagnostic(studentLearningSequences);

  const descriptionParagraphCopy = () => {
    if (studentIsRecommendedThisDiagnostic) {
      return previousAssignmentRecord ? `You've already assigned this recommended diagnostic to ${firstName}. You're all set!` : `We recommend assigning this diagnostic, as ${firstName} has not yet demonstrated full proficiency in most skills.`
    } else if (proficientDiagnosticRecord) {
      return `${firstName} demonstrated full proficiency in most skills on this diagnostic on ${formatDateToMMDDYYYY(proficientDiagnosticRecord.completed_at)}.`
    } else if (diagnosticLevel === INTERMEDIATE) {
      return `We don't recommend assigning this diagnostic yet. ${firstName} should first demonstrate full proficiency in most skills on the Starter Diagnostic.`
    }

    const proficientStarter = proficientDiagnostic(studentHistory.student_learning_sequences[STARTER])
    const proficientIntermediate = proficientDiagnostic(studentHistory.student_learning_sequences[INTERMEDIATE])

    let recommendedDiagnosticsCopy = 'Starter and Intermediate diagnostics'

    if (proficientStarter && !proficientIntermediate) {
      recommendedDiagnosticsCopy = 'Intermediate diagnostic'
    } else if (!proficientStarter && proficientIntermediate) {
      recommendedDiagnosticsCopy = 'Starter diagnostic'
    }
    return `We don't recommend assigning this diagnostic yet. ${firstName} should first demonstrate full proficiency in most skills on the ${recommendedDiagnosticsCopy}.`
  }

  const assignOrViewResultsButton = () => {
    if (!previousAssignmentRecord) {
      return <button className="quill-button green contained small focus-on-light" onClick={assignDiagnostics} type="button">{assigning ? <LightButtonLoadingSpinner /> : 'Assign'}</button>
    }

    const { activity_id, completed_at, } = previousAssignmentRecord

    if (completed_at) {
      return <a className="quill-button green contained small focus-on-light" href={`/teachers/progress_reports/diagnostic_reports/#/diagnostics/${activity_id}/classroom/${classroomId}/responses/${studentHistory.user_id}`} rel="noopener noreferrer" target="_blank">View results</a>
    }

    return (
      <Tooltip
        tooltipText="Results aren’t available yet as this student hasn’t completed this diagnostic."
        tooltipTriggerText={<button className="quill-button green contained small disabled focus-on-light" disabled={true} type="button">View results</button>}
      />
    )

  }

  function assignDiagnostics() {
    setAssigning(true)

    const params = {
      classroom_id: classroomId,
      diagnostics: [{
        unit_template_id: diagnosticLevelToUnitTemplateId(useELLDiagnostics)[diagnosticLevel],
        student_ids: (previousAssignmentsForLevel?.map(assignment => assignment.student_id) || []).concat(studentHistory.user_id)
      }]
    }

    requestPost('/teachers/progress_reports/assign_diagnostics/', params, (data) => {
      getPreviouslyAssignedDiagnosticDataForClassroom()
      setAssigning(false)
    }, (data) => {
      alert(TROUBLE_PROCESSING_MESSAGE);
      setAssigning(false)
    })
  }

  return (
    <div className="recommendation-card">
      <div>
        <div className="card-header">
          <h4>
            <b>{titleCase(diagnosticLevel)} Diagnostic</b>
            <span>Baseline Pre</span>
          </h4>
          {proficientDiagnosticRecord && <DemonstratedProficiencyFlag />}
          {studentIsRecommendedThisDiagnostic && <RecommendedFlag />}
        </div>
        <p className="description">{descriptionParagraphCopy()}</p>
      </div>
      <div>
        <p className="assignment">
          {
            previousAssignmentRecord
              ? `You assigned this diagnostic to ${firstName} on ${formatDateToMMDDYYYY(previousAssignmentRecord.created_at)}`
              : `You haven’t assigned this diagnostic to ${firstName} yet.`
          }
        </p>
        <div className="button-wrapper">
          <a className="quill-button outlined gray small focus-on-light" href={previewLink(diagnosticLevel)} rel="noopener noreferrer" target="_blank">Preview</a>
          {assignOrViewResultsButton()}
        </div>
      </div>
    </div>
  )
};

export default IndividualStudentRecommendationCard;
